import { IBounds } from "./Bounds.interface";

interface IStudioWidgetBase {
  id: string;

  label: string;

  bounds: IBounds;

  active: boolean;
}

export enum EStudioWidgetType {
  TIMER = "timer",

  COUNTDOWN = "countdown",

  DATE_TIME = "datetime",

  CRAWL = "crawl",

  SOCIALS = "socials",

  QR_CODE = "qrcode",

  TEXT = "text",

  AUDIO_VISUALIZER = "audio_visualizer",

  SUB_GOAL = "sub_goal",

  FOLLOWER_GOAL = "follower_goal",

  VIEWERS = "viewers",

  CHAT = "chat",

  PROGRESS_BAR = "progress_bar",

  PLAYLIST = "playlist",

  WEATHER = "weather",

  DONATION_GOAL = "donation_goal",

  LIVE_CAPTIONS = "live_captions",
}

export enum EAudioVisualizerSource {
  MIXED = "mixed",

  MICROPHONE = "microphone",

  PLAYLIST = "playlist",

  SYSTEM_AUDIO = "system_audio",

  VIDEO_AREA = "video_area",
}

export interface IStudioWidgetTimer extends IStudioWidgetBase {
  type: EStudioWidgetType.TIMER;

  options: {
    mode: "timer";

    /**
     * Optional baked SVG design from the Timer design store
     * (see `data/timerDesignsCatalog.ts`). When set, the runtime renders
     * that design's card SVG (with the live MM:SS substituted) at the
     * widget bounds INSTEAD of the configurable text timer below. Absent /
     * undefined = legacy behaviour, the custom text timer driven by the
     * font / color / align options. Clearing it returns to the text timer.
     */
    designId?: string;

    fontFamily?: string;

    fontSize?: string;

    color?: string;

    backgroundColor?: string;

    theme: "custom"; // Timer only supports custom theme now

    boundsMode?: "fullwidth" | "contained";

    bold?: boolean;

    italic?: boolean;

    underline?: boolean;

    strike?: boolean;

    lineHeight?: number;

    wordSpacing?: number;

    textAlign?: "left" | "center" | "right";

    showLabels?: boolean;

    showDays?: boolean;

    showHours?: boolean;

    showMinutes?: boolean;

    showSeconds?: boolean;

    padWithZeros?: boolean;
  };
}

export interface IStudioWidgetCountdown extends IStudioWidgetBase {
  type: EStudioWidgetType.COUNTDOWN;

  options: {
    mode: "countdown";

    /**
     * Optional baked SVG design from the Countdown design store
     * (see `data/countdownDesignsCatalog.ts`). When set, the runtime renders
     * that design's card SVG (with the live D/H/M/S substituted) at the
     * widget bounds INSTEAD of the configurable / themed countdown below.
     * Absent / undefined = legacy behaviour, the existing themed countdown
     * driven by the theme / font / color / format options. Clearing it
     * returns to the themed countdown.
     */
    designId?: string;

    targetDate: string;

    timezone: string;

    fontFamily?: string;

    fontSize?: string;

    color?: string;

    backgroundColor?: string;

    theme: string; // Countdown supports all themes

    bold?: boolean;

    italic?: boolean;

    underline?: boolean;

    strike?: boolean;

    lineHeight?: number;

    wordSpacing?: number;

    /** Horizontal alignment of the countdown text (legacy themed render). */
    textAlign?: "left" | "center" | "right";

    showLabels?: boolean;

    showDays?: boolean;

    showHours?: boolean;

    showMinutes?: boolean;

    showSeconds?: boolean;

    padWithZeros?: boolean;
  };
}

export interface IStudioWidgetDateTime extends IStudioWidgetBase {
  type: EStudioWidgetType.DATE_TIME;

  options: {
    designId?: string;

    dateFormat?: string;

    timezone?: string;

    timeFormat?: string;

    showSeconds?: boolean;

    fontFamily?: string;

    fontSize?: string;

    fontColor?: string;

    backgroundColor?: string;

    bold?: boolean;

    italic?: boolean;

    underline?: boolean;

    strike?: boolean;

    lineHeight?: number;

    wordSpacing?: number;

    textAlign?: "left" | "center" | "right";
  };
}

export interface IStudioWidgetCrawl extends IStudioWidgetBase {
  type: EStudioWidgetType.CRAWL;

  options: {
    /**
     * When set to a known id in `crawlDesignsCatalog`, the widget renders that
     * design's bar (fixed chrome + an animated scroll strip in the design's
     * typography) instead of the plain colour/font crawl below — the `text` and
     * `velocity` still drive it. Absent/unknown ⇒ legacy render. Backward-compat.
     */
    designId?: string;

    text: string;

    velocity: string;

    fontFamily?: string;

    fontSize?: string;

    fontColor?: string;

    backgroundColor?: string;

    bold?: boolean;

    italic?: boolean;

    underline?: boolean;

    strike?: boolean;

    lineHeight?: number;

    wordSpacing?: number;

    textAlign?: "left" | "center" | "right";
  };
}

export interface IStudioWidgetSocials extends IStudioWidgetBase {
  type: EStudioWidgetType.SOCIALS;

  options: {
    designId?: string;

    selectedIntegrations?: string[];

    theme?: string;

    fontFamily?: string;

    fontSize?: string;

    textColor?: string;

    backgroundColor?: string;

    bold?: boolean;

    italic?: boolean;

    underline?: boolean;

    strike?: boolean;

    lineHeight?: number;

    wordSpacing?: number;

    textAlign?: "left" | "center" | "right";

    integrations?: {
      twitch: string;

      youtube: string;

      kick: string;

      restream: string;

      theme: string;

      fontFamily?: string;

      fontSize?: string;

      fontColor?: string;
    };
  };
}

export interface IStudioWidgetQRCode extends IStudioWidgetBase {
  type: EStudioWidgetType.QR_CODE;

  options: {
    content: string;

    backgroundColor?: string;

    foregroundColor?: string;

    size?: number;

    textAlign?: "left" | "center" | "right";
  };
}

export interface IStudioWidgetText extends IStudioWidgetBase {
  type: EStudioWidgetType.TEXT;

  options: {
    text: string;

    fontFamily: string;

    fontSize: string;

    textColor: string;

    backgroundColor: string;

    bold: boolean;

    italic: boolean;
  };
}

export interface IAudioVisualizerSourceConfig {
  enabled: boolean;

  color: string;

  source: EAudioVisualizerSource;

  label: string;
}

export interface IStudioWidgetAudioVisualizer extends IStudioWidgetBase {
  type: EStudioWidgetType.AUDIO_VISUALIZER;

  options: {
    sources: IAudioVisualizerSourceConfig[];

    backgroundColor: string;

    barCount: number;

    barGap: number;

    barRadius: number;

    smoothing: number;

    fftSize: number;

    mirror: boolean;
  };
}

export type ViewerPlatform =
  | "twitch"
  | "youtube"
  | "kick"
  | "restream"
  | "livepeer"
  | "facebook";

/**
 * Subscriber goal tracker. Shows a horizontal progress bar with current /
 * target sub count + a configurable label. The `currentMode` toggles
 * between live data (read from `events.subCount.{scope}` via the
 * `OverlayEventStatsRepository` Dexie cache) and a manual override the
 * streamer types in.
 */

export interface IStudioWidgetSubGoal extends IStudioWidgetBase {
  type: EStudioWidgetType.SUB_GOAL;

  options: {
    /** Selected design-store card id (from subGoalDesignsCatalog), if any. */

    designId?: string;

    /** Target subscriber count (the "100" in "23 / 100"). */

    goal: number;

    /** Where the current count comes from. */

    currentMode: "auto" | "manual";

    /** When `currentMode === "auto"`, which window of the events stats to
     *  read. `session` resets when the streamer starts a new stream. */

    scope: "session" | "today" | "allTime";

    /** When `currentMode === "auto"`, which platforms count toward the
     *  goal. Empty array = nothing counts; undefined = legacy widgets,
     *  treated as "all platforms". */

    selectedPlatforms?: ViewerPlatform[];

    /** Used when `currentMode === "manual"`. */

    manualCurrent: number;

    /** Eyebrow label rendered above the progress bar. */

    label: string;

    /** Optional secondary copy under the progress bar
     *  (e.g. "Help us hit the goal!"). */

    caption?: string;

    /** Visual styling. */

    barColor: string;

    barBackgroundColor: string;

    backgroundColor: string;

    textColor: string;

    fontFamily?: string;

    fontSize?: string;

    /** Whether to render the percentage inline (e.g. "23%"). */

    showPercent?: boolean;

    bold?: boolean;

    italic?: boolean;
  };
}

/**
 * Follower goal. Same shape as the subscriber goal but counts `follow`
 * events instead of `sub` events. In `auto` mode it reads the running
 * tally of NEW follows (read from `events.followCount.{scope}` via the
 * `OverlayEventStatsRepository` Dexie cache) — there is no live
 * platform-side total follower count, so this tracks follows gained since
 * tracking began. `manual` lets the streamer type the current count.
 */
export interface IStudioWidgetFollowerGoal extends IStudioWidgetBase {
  type: EStudioWidgetType.FOLLOWER_GOAL;

  options: {
    /**
     * When set to a known id in `followerGoalDesignsCatalog`, the widget renders
     * that design's CARD SVG (with the live current/target/percent substituted)
     * at the widget bounds — overriding the colour/label/typography options
     * below. Absent/unknown ⇒ the plain Pixi bar render is used unchanged.
     * Backward-compatible optional field.
     */
    designId?: string;

    /** Target follower count (the "100" in "23 / 100"). */
    goal: number;

    /** Where the current count comes from. */
    currentMode: "auto" | "manual";

    /** When `currentMode === "auto"`, which window of the events stats to
     *  read. `session` resets when the streamer starts a new stream. */
    scope: "session" | "today" | "allTime";

    /** When `currentMode === "auto"`, which platforms count toward the
     *  goal. Empty array = nothing counts; undefined = legacy widgets,
     *  treated as "all platforms". */
    selectedPlatforms?: ViewerPlatform[];

    /** Used when `currentMode === "manual"`. */
    manualCurrent: number;

    /** Eyebrow label rendered above the progress bar. */
    label: string;

    /** Optional secondary copy under the progress bar. */
    caption?: string;

    /** Visual styling. */
    barColor: string;
    barBackgroundColor: string;
    backgroundColor: string;
    textColor: string;
    fontFamily?: string;
    fontSize?: string;
    /** Whether to render the percentage inline (e.g. "23%"). */
    showPercent?: boolean;
    bold?: boolean;
    italic?: boolean;
  };
}

/**
 * Live viewers counter. Aggregates viewer numbers from the platforms in
 * `selectedPlatforms` (read from `usePlatformVars()` which polls
 * `/api/integrations/viewers` every 30s). 1 platform → just that
 * count; 2+ → sum, with optional per-platform breakdown row.
 */

export interface IStudioWidgetViewers extends IStudioWidgetBase {
  type: EStudioWidgetType.VIEWERS;

  options: {
    /** Selected design-store card id (from viewersDesignsCatalog), if any. */

    designId?: string;

    /** Which platforms to include in the count. Empty = nothing shown. */

    selectedPlatforms: ViewerPlatform[];

    /** Eyebrow label. Empty string falls back to "Viewers" or the
     *  single platform's name when only one is selected. */

    label: string;

    /** Render the live dot next to the count. */

    showIcon: boolean;

    /** Render the label text above the count. */

    showLabel: boolean;

    /** When 2+ platforms selected, also list per-platform numbers. */

    showPlatformBreakdown: boolean;

    /** Display "1.2k" instead of "1234". */

    compactNumber: boolean;

    /** Visual styling. */

    backgroundColor: string;

    textColor: string;

    accentColor: string;

    fontFamily?: string;

    fontSize?: string;

    bold?: boolean;

    italic?: boolean;

    textAlign?: "left" | "center" | "right";
  };
}

export type IChatPlatform =
  | "twitch"
  | "youtube"
  | "kick"
  | "facebook"
  | "restream";

export type IChatLayout = "compact" | "card";

/**
 * Snapshot of a chat message that the streamer pinned. Stored in the
 * widget's options so the pinned message persists across reloads even
 * when the original message has scrolled out of the live buffer. The
 * shape mirrors the relevant subset of `ChatMessage` from the eventBus.
 */

/** Hard cap on how many messages can be pinned simultaneously. */

export const CHAT_PINNED_MAX = 5;

export interface IPinnedChatMessage {
  id: string;

  platform: string;

  authorName: string;

  authorColor?: string;

  authorAvatar?: string;

  text: string;

  emotes?: Array<{
    name: string;

    url: string;

    positions: [number, number][];
  }>;
}

/**
 * Multi-platform live chat. Reads from the unified chat stream that the
 * extension server already aggregates (consumed via `eventBusService` /
 * `atomChatBuffer`). No per-platform integration needed at the widget level.
 *
 * Phase 1 ships the React editor preview + config modal. The Pixi broadcast
 * renderer is a stub that mounts the widget container but doesn't draw yet —
 * that lands in Phase 2.
 */

export interface IStudioWidgetChat extends IStudioWidgetBase {
  type: EStudioWidgetType.CHAT;

  options: {
    /**
     * When set to a known id in `chatDesignsCatalog`, the widget renders that
     * design's PANEL SVG (with the live messages laid out per the design) at
     * the widget bounds — overriding the colour/layout/font options below. The
     * platform/blocked/slow-mode filters still apply. Absent/unknown ⇒ the
     * legacy Pixi chat render is used unchanged. Backward-compatible optional.
     */
    designId?: string;

    /** Filter to selected platforms; `undefined` or empty array = all. */

    platforms?: IChatPlatform[];

    /** Max visible messages on screen. Default 12. Clamped 5–30. */

    maxMessages?: number;

    /** Auto-fade messages after N seconds. 0 = never. Default 0. */

    fadeAfterSec?: number;

    /**
     * Minimum ms between messages becoming visible. Useful when chat is
     * exploding and nobody can read it. Excess messages are queued and
     * released at this cadence (queue capped at 50, oldest dropped).
     * 0 = off. Default 0.
     */

    slowModeMs?: number;

    /** Show platform badge next to author name. Default true. */

    showPlatformBadge?: boolean;

    /**
     * Show a thin platform-color stripe along the row's left edge so chat
     * is segregated by source from peripheral vision (independent from the
     * dot badge). Default false.
     */

    showPlatformAccent?: boolean;

    /**
     * Tint the row background with the platform's color at low alpha (~10%)
     * so messages from different platforms read as distinct cards. Stacks
     * behind the highlight tint when both apply. Default false.
     */

    showPlatformBg?: boolean;

    /**
     * Speak `isHighlighted` messages out loud via the browser's Speech
     * Synthesis API. Useful for TTS-on-donation flows. Default false.
     */

    ttsHighlighted?: boolean;

    /**
     * Voice name (matches `SpeechSynthesisVoice.name` from
     * `speechSynthesis.getVoices()`) to use when `ttsHighlighted` is on.
     * Empty / undefined = system default voice.
     */

    ttsVoice?: string;

    /**
     * Pinned chat messages — rendered fixed at the top of the chat,
     * separate from the rolling buffer. Snapshotted into the widget so
     * pins persist even after the original messages scroll out of the
     * buffer. Capped at PINNED_MAX (5) — older pins drop on overflow.
     */

    pinnedMessages?: IPinnedChatMessage[];

    /** Show author avatar (when provided by the chat feed). Default true. */

    showAvatar?: boolean;

    /** Inline emote rendering. Default true. */

    showEmotes?: boolean;

    /** Hide messages from listed user names (case-insensitive exact match). */

    blockedUsers?: string[];

    /** Hide messages whose body matches one of these regexes. */

    blockedPatterns?: string[];

    /** Visual */

    fontFamily?: string;

    fontSize?: string;

    backgroundColor?: string;

    /** Override per-message platform color when set. */

    authorColor?: string;

    messageColor?: string;

    /** "compact" = single-line; "card" = author + body stacked. Default "card". */

    layout?: IChatLayout;

    /** Theme key — same surface as the rest of the widgets. */

    theme?: string;
  };
}

/**
 * Decorative / generic progress bar. Unlike `IStudioWidgetSubGoal` which
 * is tied to subscriber counts, this is a free-form progress bar the
 * streamer drives manually or via wall-clock between `startDate` and
 * `targetDate`. The fill is a solid colour — gradient support was
 * removed because Pixi v8's `FillGradient` rendered visibly different
 * from CSS in too many edge cases (color-space interpolation, stop
 * positioning, gloss compositing). Kept the optional vertical alpha
 * gloss because that's a simple two-stop white→transparent overlay and
 * renders consistently.
 */
export interface IStudioWidgetProgressBar extends IStudioWidgetBase {
  type: EStudioWidgetType.PROGRESS_BAR;

  options: {
    /**
     * When set to a known id in `progressBarDesignsCatalog`, the widget renders
     * that design's CARD SVG (from the local catalog) stretched to the widget
     * bounds, driven by the same start↔target fill percentage — overriding the
     * legacy colour/label/typography options below. Absent/unknown ⇒ the plain
     * Pixi bar render is used unchanged. Backward-compatible optional field.
     */
    designId?: string;

    /**
     * Bar reads 0% at `startDate` and 100% at `targetDate`. ISO strings.
     * Ratio = `(now - startDate) / (targetDate - startDate)` clamped to
     * [0, 1]. When seeded via a scene template the materialiser
     * substitutes `"__APPLY_TIME__"` / `"__APPLY_TIME_PLUS_5MIN__"`
     * placeholders with the actual apply timestamps so the bar fills
     * automatically as a paired countdown empties.
     *
     * If either date is missing or invalid the bar renders empty (ratio=0).
     */
    startDate?: string;
    targetDate?: string;

    /**
     * Upper bound for the displayed value when using `{value}` in the
     * label. Default 100 (so `{value}` matches `{percent}`). Doesn't
     * affect the bar fill ratio — that always spans the full
     * start↔target window.
     */
    max?: number;

    /**
     * Optional label rendered inside the bar. Supports `{percent}` and
     * `{value}` placeholders. Empty = no label.
     */
    label?: string;

    /** Append `· XX%` to the label when no `{percent}` placeholder is set. */
    showPercent?: boolean;

    /** Solid fill color. */
    barColor: string;

    /** Background of the unfilled track area. */
    barBackgroundColor: string;

    /** Outer container background (behind the track). */
    backgroundColor: string;

    /** Optional outer border around the container. */
    borderColor?: string;
    borderWidth?: number;

    /**
     * Container border radius in px, or `"pill"` to use container-height/2.
     * Default `"pill"`.
     */
    borderRadius?: number | "pill";

    /** Show a soft white gloss highlight on top of the fill (Y2K look). */
    showGloss?: boolean;

    /** Outer container drop-shadow / glow color. Empty = no glow. */
    glowColor?: string;

    /** Label styling. */
    textColor: string;
    fontFamily?: string;
    fontSize?: string;
    bold?: boolean;
    italic?: boolean;
    /** Letter spacing for the inline label (e.g. `0.4em`). */
    letterSpacing?: string;
    /** Label alignment inside the bar. Default `"center"`. */
    textAlign?: "left" | "center" | "right";
  };
}

/**
 * A single row painted by the Playlist widget. The renderers compose this
 * shape from the live Playlist DB via `buildLivePlaylistRows` — it isn't
 * authored by hand. `prefix` and `separator` are optional so a row can be
 * just the title for cleaner lineup-style listings.
 *
 * Example rendered output for `{ prefix: "TR. 01", separator: "→", title: "saludos y café" }`:
 *
 *     TR. 01  →  saludos y café
 */
export interface IStudioWidgetPlaylistRow {
  prefix?: string;
  separator?: string;
  title: string;
}

export interface IStudioWidgetPlaylist extends IStudioWidgetBase {
  type: EStudioWidgetType.PLAYLIST;

  options: {
    /** Selected design-store card id (from playlistDesignsCatalog), if any. */
    designId?: string;

    /**
     * Format string for the prefix. Supports placeholders `{number}` and
     * `{index}` (1-based queue position). Defaults to `"{number}"` so a
     * track with `number: 3` renders as just `"3"`. Examples:
     * `"TR. {number}"`, `"#{index}"`, `"▶"` (literal, same prefix on
     * every row).
     */
    prefixFormat?: string;

    /**
     * Format string for the title. Supports `{name}`, `{artist}`,
     * `{album}`. Defaults to `"{name}"`. Examples:
     * `"{artist} — {name}"`, `"{name} · {album}"`.
     */
    titleFormat?: string;

    /**
     * Cap the number of rows shown. Useful for a fixed-height widget on a
     * busy queue. `0` / unset = no cap.
     */
    maxRows?: number;

    /**
     * Vertical gap between rows, in 1920-design px. Stacks on top of the
     * row's natural `lineHeightPx`. Defaults to `fontSize * 0.4` when
     * unset — comfortable for sans-serif setlists.
     */
    rowGap?: number;

    /**
     * Column width reserved for the `prefix` so titles align in a tabular
     * column even when prefixes vary in length ("TR. 01" vs "TR. 100").
     * 1920-design px. When 0 / unset, prefix and separator flow inline
     * with the title (no column).
     */
    prefixMinWidth?: number;

    /**
     * Optional override for the prefix colour. Falls back to `textColor`
     * when unset, matching the typical "monochrome list" look. Use this
     * for accent colours like a brand-yellow track number against a white
     * title.
     */
    prefixColor?: string;

    fontFamily?: string;
    fontSize?: string;
    textColor?: string;
    backgroundColor?: string;

    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;

    lineHeight?: number;
    wordSpacing?: number;

    textAlign?: "left" | "center" | "right";
  };
}

/**
 * Weather widget — pulls current conditions + short forecast from
 * Open-Meteo (https://open-meteo.com). No API key needed; the service
 * rate-limits by IP, so every streamer's machine gets its own 10k
 * req/day quota and Eriol incurs zero cost or auth plumbing.
 *
 * The location is captured up-front in the config (city autocomplete
 * via Open-Meteo's geocoding endpoint), then stored as resolved
 * `latitude`/`longitude`/`timezone`/`displayName` so the runtime
 * widget can fetch directly without re-resolving the city each tick.
 */
/**
 * Visual theme IDs for the weather widget. `custom` keeps the
 * runtime in "free style" mode (uses the explicit font / color /
 * align options below); every other ID is a pre-designed SVG theme
 * with its own typography + palette baked in — the font/color
 * controls are ignored when one of those is selected.
 */
export type WeatherThemeId =
  | "custom"
  | "dark"
  | "news"
  | "y2k"
  | "cyber"
  | "editorial"
  | "anime"
  | "pixel"
  | "cottage"
  | "brutalist"
  | "vapor";

export interface IStudioWidgetWeather extends IStudioWidgetBase {
  type: EStudioWidgetType.WEATHER;

  options: {
    /** Visual theme. Defaults to `custom` (free-style render driven
     *  by the font/color/align options below). Picking any other
     *  theme switches the runtime to a baked SVG design and ignores
     *  the style options. */
    theme?: WeatherThemeId;

    /** Display label shown next to the current temp. Usually the city
     *  name returned by the geocoder ("Buenos Aires"). */
    displayName?: string;

    /** Resolved coordinates from the geocoding step. Without these
     *  the widget renders a "Pick a city" empty state instead of
     *  pinging the API with `0,0`. */
    latitude?: number;
    longitude?: number;

    /** IANA timezone the API should use for the daily forecast bucket
     *  boundaries — defaults to `auto` (server-derived from lat/lon)
     *  when unset, which is the right call for most users. */
    timezone?: string;

    /** Temperature unit. `celsius` is the API default. */
    units?: "celsius" | "fahrenheit";

    /** Show the next-N-days forecast row below the current readout.
     *  When false only the current condition + temp is rendered. */
    showForecast?: boolean;

    /** Minimal mode: render ONLY the current temperature (e.g. "12°"),
     *  no icon / condition / city / forecast. Lets the widget stand in
     *  for a compact lower-third temp readout (used by the scene
     *  templates that had a static "12°" text). Ignored when a baked
     *  `theme` is selected. Default false. */
    tempOnly?: boolean;

    /** Number of forecast days to render (1–3). Ignored if
     *  `showForecast` is false. */
    forecastDays?: 1 | 2 | 3;

    /** How often the runtime re-fetches conditions. Defaults to
     *  15 min. Lower bound enforced at 5 min by the runtime to
     *  stay polite with Open-Meteo's quota. */
    pollIntervalMs?: number;

    /** Visual options, mirror the rest of the text-heavy widgets. */
    fontFamily?: string;
    fontSize?: string;
    fontColor?: string;
    backgroundColor?: string;
    bold?: boolean;
    italic?: boolean;
    textAlign?: "left" | "center" | "right";
  };
}

/**
 * Donation Goal widget — mirrors SubGoal but tracks USD raised via
 * connected donation providers (Ko-fi / BMC) instead of subs.
 *
 * Data flow (auto mode): backend SSE `/donations/stream` pushes each
 * `donation.created` and `donation.refunded` event to the runtime.
 * Widget keeps a running tally filtered by `scope` and `providers`
 * and re-renders the bar fill + label. No polling — pure push.
 *
 * Manual mode: streamer types `manualCurrent` directly. Useful before
 * the SSE wiring is verified, during testing, or when the streamer
 * wants to spotlight an off-platform donation total (cash, crypto).
 */
/**
 * Theme key for the donation goal widget — mirrors WeatherThemeId. `custom`
 * (default) keeps the configurable DOM bar; any named theme switches the
 * runtime to a baked SVG design (see DonationGoalWidget/themes.tsx) and
 * ignores the style options.
 */
export type DonationThemeId =
  | "custom"
  | "dark"
  | "kofi"
  | "bmc"
  | "y2k"
  | "cyber"
  | "editorial"
  | "pixel"
  | "cottage"
  | "brutalist"
  | "anime";

export interface IStudioWidgetDonationGoal extends IStudioWidgetBase {
  type: EStudioWidgetType.DONATION_GOAL;

  options: {
    /** Target raised amount in USD cents (the "100" in "USD 23 / 100"). */
    goalUsdCents: number;

    /** Which window aggregates. `session` resets when the streamer starts a
     *  new stream; `today` resets at local midnight; `allTime` is lifetime. */
    scope: "session" | "today" | "allTime";

    /** Which donation providers count. Empty array = nothing counts;
     *  undefined = legacy widgets, treated as "all providers". */
    selectedProviders?: Array<"kofi" | "bmc">;

    /** Eyebrow label rendered above the progress bar. */
    label: string;

    /** Optional caption under the bar (e.g. "¡Ayudanos a llegar!"). */
    caption?: string;

    /** Theme key. `custom` (default) uses the configurable bar below; a
     *  named theme renders a baked SVG design and ignores the style options. */
    theme?: DonationThemeId;

    /** Visual styling — mirrors SubGoal. */
    barColor: string;
    barBackgroundColor: string;
    backgroundColor: string;
    textColor: string;
    fontFamily?: string;
    fontSize?: string;

    /** Render the percentage inline (e.g. "23%"). */
    showPercent?: boolean;

    /** Render the latest donor's name inline (e.g. "Último: @juan $5"). */
    showLatestDonor?: boolean;

    bold?: boolean;
    italic?: boolean;
  };
}

/**
 * Live Captions widget — burns real-time ASR captions of the FULL program
 * audio mix into the scene. The transcription runs in a local Python
 * sidecar (faster-whisper / CUDA); this widget only configures + renders
 * the result. The widget's `active` flag toggles the ASR session on/off
 * (a single program-wide stream regardless of how many caption widgets
 * exist — they all read the same `captionTextAtom`).
 */
export interface IStudioWidgetLiveCaptions extends IStudioWidgetBase {
  type: EStudioWidgetType.LIVE_CAPTIONS;

  options: {
    /**
     * Which ASR backend transcribes the program audio. `"faster-whisper"`
     * (default) is multilingual + translate; `"parakeet-tdt-v3"` is the
     * heavier NVIDIA Parakeet ONNX model (25 langs, native timestamps).
     * Legacy widgets predating the engine selector omit this and are
     * treated as `"faster-whisper"` by the runtime.
     */
    engine?: "faster-whisper" | "parakeet-tdt-v3";

    /** BCP-47 language hint, or `null` for auto-detect. */
    lang: string | null;

    /** `transcribe` keeps the source language; `translate` → English. */
    task: "transcribe" | "translate";

    /** How many lines of caption to keep on screen (>= 1). */
    maxLines: number;

    /** Visual styling. */
    fontFamily?: string;

    /** Design-px font size against the 1920-wide canvas. */
    fontSize?: number;

    color?: string;

    /** Draw a semi-opaque plate behind the text for legibility. */
    background?: boolean;
  };
}

export type IStudioWidget =
  | IStudioWidgetTimer
  | IStudioWidgetCountdown
  | IStudioWidgetDateTime
  | IStudioWidgetCrawl
  | IStudioWidgetSocials
  | IStudioWidgetQRCode
  | IStudioWidgetText
  | IStudioWidgetAudioVisualizer
  | IStudioWidgetSubGoal
  | IStudioWidgetFollowerGoal
  | IStudioWidgetViewers
  | IStudioWidgetChat
  | IStudioWidgetProgressBar
  | IStudioWidgetPlaylist
  | IStudioWidgetWeather
  | IStudioWidgetDonationGoal
  | IStudioWidgetLiveCaptions;
